import { Checkbox, FormControlLabel } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { MatchedDefect, OptionValue } from '../../../actions/defectAssistant/types'
import { colors } from '../../../Theme'
import {
  FieldLabel,
  FieldPair,
  FieldValue,
  MatchedDefectCard,
  OptionDesc,
  OptionsList,
  OptionState,
  OptionTitle,
  ScrollOptionButton,
  TEXT_PRIMARY,
  TITLE_DIMMED,
} from './styles'

// Stable identity for an option/answer — value if present, otherwise the label.
export const optionKey = (opt: { value?: string | number | null; label?: string | null }): string =>
  String(opt.value ?? opt.label)

// Option list renderer — same layout in all states, only colors differ.
export const renderOptions = (
  options: OptionValue[],
  getState: (opt: OptionValue) => OptionState,
  onClick?: (opt: OptionValue) => void
): JSX.Element => (
  <OptionsList>
    {options.map((opt, idx) => {
      const s = getState(opt)
      return (
        <ScrollOptionButton
          key={String(opt.value ?? idx)}
          type="button"
          optionState={s}
          onClick={() => s === 'active' && onClick?.(opt)}
        >
          <OptionTitle dimmed={s === 'dimmed'}>{opt.label ?? ''}</OptionTitle>
          {opt.additionalInfo && (
            <OptionDesc dimmed={s === 'dimmed'}>{opt.additionalInfo}</OptionDesc>
          )}
        </ScrollOptionButton>
      )
    })}
  </OptionsList>
)

// The matched open-defect card, shown both in the active duplicate-check and its locked summary.
export const MatchedDefectInfo = ({
  matched,
  dimmed,
}: {
  matched: MatchedDefect
  dimmed?: boolean
}): JSX.Element => {
  const { t } = useTranslation()
  return (
    <MatchedDefectCard style={dimmed ? { opacity: 0.35 } : undefined}>
      <FieldPair>
        <FieldLabel>{t('defect.aiAssistant.duplicate.existingDefect')}</FieldLabel>
        <FieldValue>
          {matched.id}
          {matched.description ? ` — ${matched.description}` : ''}
        </FieldValue>
      </FieldPair>
      {matched.comments && (
        <FieldPair>
          <FieldLabel>{t('defect.aiAssistant.duplicate.comments')}</FieldLabel>
          <FieldValue>{matched.comments}</FieldValue>
        </FieldPair>
      )}
      {matched.status && (
        <FieldPair>
          <FieldLabel>{t('defect.aiAssistant.duplicate.status')}</FieldLabel>
          <FieldValue>{matched.status}</FieldValue>
        </FieldPair>
      )}
    </MatchedDefectCard>
  )
}

// Checkbox list shared by the active multiselect and its locked summary. Omit onToggle to render the
// list disabled (locked); selected labels then stay readable (#333) and unselected dim to grey.
export const CheckboxOptionList = ({
  uuid,
  options,
  isChecked,
  onToggle,
}: {
  uuid: string
  options: OptionValue[]
  isChecked: (opt: OptionValue, idx: number) => boolean
  onToggle?: (idx: number) => void
}): JSX.Element => {
  const disabled = !onToggle
  return (
    <OptionsList>
      {options.map((opt, idx) => {
        const checked = isChecked(opt, idx)
        return (
          <FormControlLabel
            key={`${uuid}-${idx}`}
            control={
              <Checkbox
                checked={checked}
                disabled={disabled}
                onChange={onToggle ? () => onToggle(idx) : undefined}
                sx={{ padding: '4px 9px 4px 0', '&.Mui-checked': { color: colors.purple20 } }}
              />
            }
            label={opt.label ?? ''}
            sx={(muiTheme) => ({
              alignItems: 'center',
              marginLeft: 0,
              marginBottom: '4px',
              // Locked rows are non-interactive; pointer-events:none lets a click fall through to the
              // enclosing section so it re-opens the question (disabled inputs otherwise swallow it).
              ...(disabled && { pointerEvents: 'none' }),
              '& .MuiFormControlLabel-label': {
                color: muiTheme.palette.mode === 'dark' ? colors.white : TEXT_PRIMARY,
              },
              '& .MuiFormControlLabel-label.Mui-disabled': {
                color:
                  muiTheme.palette.mode === 'dark'
                    ? colors.white
                    : checked
                    ? TEXT_PRIMARY
                    : TITLE_DIMMED,
              },
            })}
          />
        )
      })}
    </OptionsList>
  )
}
